
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useAnalytics} from "@common-core/react-analytics";
import {
  HomeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  Squares2x2Icon
} from "@interstate/components/Icons";
import {useEnvironment} from "@common-core/react-runtime/context";
import {Environment} from "../runtime";
import {SidebarItem} from "../layout";
import {menuLabelKeyFor, pageTitleKeyFor} from "../pbc";
import {useNavigation} from "./useNavigation";

export type Page =
  | "home"
  | "search"
  | "client-search"
  | "client-hierarchy-search"
  | "create-new-entity";

export interface SidebarContext {
  page?: Page | string;
}

export const useSidebarItems = ({page}: SidebarContext = {}): SidebarItem[] => {
  const {t} = useTranslation();
  const {navigateTo, navigateToHome, navigateToSearch, navigateToClientSearch} =
    useNavigation();
  const {productEvent} = useAnalytics();
  const environment = useEnvironment<Environment>();

  // Define the static items always present in the sidebar
  const staticItems = useMemo<SidebarItem[]>(
    () => [
      {
        id: "home",
        active: page === "home",
        labelKey: "navigation.home",
        icon: HomeIcon,
        onClick: event => {
          productEvent({
            name: "navigation_item_clicked",
            properties: {
              location: "Sidebar",
              value: "Home",
              result: "Navigated to Home"
            }
          });
          navigateToHome(event);
        }
      },
      {
        id: "search",
        active: page === "search",
        labelKey: "navigation.search",
        icon: MagnifyingGlassIcon,
        onClick: event => {
          productEvent({
            name: "navigation_item_clicked",
            properties: {
              location: "Sidebar",
              value: "Operational Search",
              result: "Navigated to Operational Search"
            }
          });
          navigateToSearch(event);
        }
      },
      {
        id: "client-hierarchy-search",
        active: page === "client-hierarchy-search",
        labelKey: "navigation.create-orgs",
        icon: Squares2x2Icon,
        onClick: event => {
          productEvent({
            name: "navigation_item_clicked",
            properties: {
              location: "Sidebar",
              value: "Client Hierarchy Search",
              result: "Navigated to Client Hierarchy Search"
            }
          });
          navigateToClientSearch(event);
        }
      },
      {
        id: "create-new-entity",
        active: page === "create-new-entity",
        labelKey: "navigation.create-new-entity",
        featureFlag: "pdt.developer-entity-self-service",
        icon: PlusIcon,
        onClick: event => {
          productEvent({
            name: "navigation_item_clicked",
            properties: {
              location: "Sidebar",
              value: "Create Test Entity",
              result: "Navigated to Create Test Entity"
            }
          });
          navigateTo(event, "/create-new-entity/");
        }
      }
    ],
    [page]
  );

  // Inject dynamic items based on the environment configuration
  const dynamicItems = useMemo<SidebarItem[]>(
    () =>
      environment.sidebarComponents?.map(component => ({
        id: component.id,
        active: page === component.id,
        labelKey: menuLabelKeyFor(component),
        icon: component.icon,
        featureFlag: component.featureFlag,
        permissions: component.permissions,
        whenAuthorized: component.whenAuthorized,
        whenUnauthorized: component.whenUnauthorized,
        onClick: event => {
          productEvent({
            name: "navigation_item_clicked",
            properties: {
              location: "Sidebar",
              value: t(pageTitleKeyFor(component)),
              result: `Navigated to ${t(pageTitleKeyFor(component))}`
            }
          });
          navigateTo(event, component.path);
        }
      })) || [],
    [page, environment.sidebarComponents]
  );

  // Return the combined list of static and dynamic items
  return useMemo<SidebarItem[]>(
    () => [...staticItems, ...dynamicItems],
    [staticItems, dynamicItems]
  );
};
